type Listener = (message: any) => void;

class WsBus {
    private ws?: WebSocket;
    private readonly subs = new Set<Listener>();
    private reconnectTimer?: number;

    constructor(private readonly url: string) {}

    private dispatch(message: any) {
        for (const fn of Array.from(this.subs)) {
            try {
                fn(message);
            } catch (error) {
                console.warn('[WsBus] listener error', error);
            }
        }
    }

    connect() {
        if (!this.url) {
            console.error('[WsBus] missing websocket URL');
            return;
        }
        if (
            this.ws &&
            (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)
        ) {
            return;
        }

        if (this.reconnectTimer) {
            clearTimeout(this.reconnectTimer);
            this.reconnectTimer = undefined;
        }

        this.ws = new WebSocket(this.url);

        this.ws.onopen = () => {
            this.dispatch({ t: '__status', status: 'connected' });
        };

        this.ws.onmessage = (event: MessageEvent) => {
            try {
                const message = JSON.parse(String(event.data));
                this.dispatch(message);
            } catch (error) {
                console.warn('[WsBus] failed to parse message', error);
            }
        };

        this.ws.onerror = event => {
            console.error('[WsBus] socket error', event);
        };

        this.ws.onclose = () => {
            this.dispatch({ t: '__status', status: 'disconnected' });
            this.reconnectTimer = window.setTimeout(() => this.connect(), 1000);
        };
    }

    subscribe(listener: Listener) {
        this.subs.add(listener);
        return () => {
            this.subs.delete(listener);
        };
    }

    sendIntent(name: string, args?: any, cid?: string) {
        const intentId = cid || `c_${Date.now()}_${Math.floor(Math.random() * 1e6)}`;
        const payload = {
            t: 'intent' as const,
            name,
            args,
            cid: intentId,
            ts: Date.now(),
        };

        if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
            console.warn('[WsBus] socket not ready, reconnecting');
            this.connect();
            setTimeout(() => {
                if (this.ws && this.ws.readyState === WebSocket.OPEN) {
                    this.ws.send(JSON.stringify(payload));
                }
            }, 100);
        } else {
            this.ws.send(JSON.stringify(payload));
        }

        return intentId;
    }
}

const resolveUrl = () => {
    const baseUrl =
        import.meta.env.VITE_HIVECORE_API_URL ||
        import.meta.env.VITE_BACKEND_URL ||
        'https://hivecore-backend-production.up.railway.app';
    return (
        import.meta.env.VITE_WS_URL ||
        import.meta.env.VITE_HIVECORE_WS_URL ||
        `${baseUrl.replace(/^http/, 'ws')}/ws`
    );
};

export const globalBus = new WsBus(resolveUrl());

export type GlobalBus = typeof globalBus;
